import { debounce, has } from 'lodash';
import { ProductCard } from '../common/product-card-class';
import { initializeProductOption } from './product-item';

const init = async () => {
  const section = cacheElements(); // Cache DOM elements for better performance;
  if (!section) return;
  initLoadedProducts(state.elements.firstLoadProducts); // Initialize products for the first load
  await loadPrevPages(); // Load previous pages asynchronously
  addEventListeners(); // Add event listeners to interact with the page
  document.addEventListener("DOMContentLoaded", scrollToLastProduct);
  window.addEventListener('load', scrollToLastProduct);
  window.addEventListener('pageshow', scrollToLastProduct);
};


const state = {
  loading: false,
  observing: false,
  parser: new DOMParser(),
  searchParams: new URLSearchParams(window.location.search),
  productCountBeforeBtn: window.Collection.productCountBeforeBtn,
  isInfiniteScroll: window.Collection.isInfiniteScroll,
  observerOptions: {
    root: null,
    rootMargin: '0px 0px 500px 0px',
    threshold: 0,
  },
};

const cacheElements = () => {
  const sectionWrapper = document.getElementById(`section-wrapper-${window.Collection.section.id}`)
  if (!sectionWrapper) return null;
  const elements = {
    sectionWrapper: sectionWrapper,
    collectionsContainer: sectionWrapper.querySelector('.collections'),
    target: sectionWrapper.querySelector('.scroll-target'),
    collectionList: sectionWrapper.querySelector('.collections__list'),
    loader: sectionWrapper.querySelector('.collections__loader'),
    filtersCount: sectionWrapper.querySelector('.filters__count'),
    filtersDrawer: sectionWrapper.querySelector('.filters__drawer'),
    emptyCollectionContainer: sectionWrapper.querySelector('.collections__empty'),
    selectedFiltersContainer: sectionWrapper.querySelector('.filters__selected'),
    loadMoreBtn: sectionWrapper.querySelector('.Button--load-more'),
    firstLoadProducts: sectionWrapper.querySelectorAll('.product-grid-item'),
  };
  // Extract the current page from the search parameters
  state.currentPage = +state.searchParams.get('page') || 1;
  // Create an IntersectionObserver
  state.observer = createIntersectionObserver(elements, onScrollBottom);
  // Store the elements in the state
  state.elements = elements;
  return sectionWrapper;
};

const addEventListeners = () => {
  scrollToLastProduct();
  initObserver();
  document.addEventListener('product-grid:param:change', onParamChange);
  state.elements.loadMoreBtn?.addEventListener('click', onLoadMoreBtn)
};

// LOAD PREVIOUS PAGES HELPERS

const loadPrevPages = async () => {
  if (state.currentPage === 1) return;

  const loadedProducts = await loadProductsFromPreviousPages();
  const sortedProducts = sortLoadedProducts(loadedProducts);

  if (sortedProducts.length > 0) {
    updateUIAfterLoading(sortedProducts);
  }
};

async function loadProductsFromPreviousPages() {
  const promises = [];
  const loadedProducts = [];

  for (let currentLoadPage = 1; currentLoadPage < state.currentPage; currentLoadPage++) {
    promises.push(
      fetchAndPushProducts(currentLoadPage, loadedProducts)
    );
  }

  await Promise.all(promises);
  return loadedProducts;
}

async function fetchAndPushProducts(currentLoadPage, loadedProducts) {
  const targetUrl = getFetchUrl(currentLoadPage);
  const htmlMarkup = await fetchProducts(targetUrl);
  if (!htmlMarkup) return;
  const data = cacheElementsFromResponse(htmlMarkup);

  loadedProducts.push(...data.elements.products);
  addProductsToWindow(htmlMarkup);
}

function sortLoadedProducts(loadedProducts) {
  return loadedProducts.toSorted(
    (a, b) => parseFloat(b.dataset.index, 10) - parseFloat(a.dataset.index, 10)
  );
}

function updateUIAfterLoading(loadedProducts) {
  initLoadedProducts(loadedProducts, true, 'afterbegin');
  state.elements.collectionsContainer.classList.remove('loading');
  scrollToLastProduct();
  initializeProductOption();
  window.addEventListener('load', scrollToLastProduct);
}

// INTERSECTION HELPERS

const createIntersectionObserver = (elements, callback) => {
  return new IntersectionObserver(callback, state.observerOptions);
};

const initObserver = () => {
  // If infinite scrolling is disabled, exit early
  if (!state.isInfiniteScroll) return;
  // Observe the target element with the specified options
  state.observer.observe(state.elements.target, state.observerOptions);
  // Update the observing flag to indicate that observation has started
  state.observing = true;
};

const disconnectObserver = () => {
  state.observer.disconnect();
  state.observing = false;
}

// LOAD MORE HELPERS

const onLoadMoreBtn = () => {
  state.elements.loadMoreBtn.classList.add('hidden');
  onScrollBottom();
}

const hideLoadMoreBtn = (force) => {
  if (!force && state.isInfiniteScroll) return;
  state.elements.loadMoreBtn.classList.toggle('hidden', force);
}

// FILTERS RELATED HELPERS 

const updateFilters = (resultCount, filtersDrawer) => {
  state.elements.filtersCount.textContent = resultCount;

  const filtersStatus = {};
  const filterOptions = filtersDrawer.querySelectorAll('.filters__option-item');

  filterOptions.forEach((option) => {
    const { key, handle } = option.dataset;
    const optionStatus = !option.classList.contains('disabled');
    filtersStatus[key] = {
      ...(filtersStatus[key] || {}),
    };
    filtersStatus[key][handle] = optionStatus;
  });

  document.dispatchEvent(
    new CustomEvent('product-grid:filters:loaded', {
      detail: { filtersStatus },
    })
  );
};

const updateSelectedFilters = (htmlMarkup) => {
  const selectedFiltersContainer =
    htmlMarkup.querySelector('.filters__selected');
  if (!selectedFiltersContainer) return;
  const isHidden = selectedFiltersContainer.classList.contains('hidden');
  state.elements.selectedFiltersContainer.innerHTML =
    selectedFiltersContainer.innerHTML;
  state.elements.selectedFiltersContainer.classList[
    isHidden ? 'add' : 'remove'
  ]('hidden');
  state.elements.selectedFiltersContainer.classList.remove('loading');
};

// HELPERS FOR GETTING NEW PRODUCTS

const fetchProducts = async (targetUrl) => {
  try {
    const response = await fetch(targetUrl).then((response) => response.text());
    const htmlMarkup = new DOMParser().parseFromString(response, 'text/html');
    return htmlMarkup;
  } catch (error) {
    console.log(`Error: Unable to fetch products at "fetchProducts". ${error.message}`);
    return null;
  } finally {
    state.elements.loader.classList.remove('active');
    hideLoadMoreBtn(false)
  }
};

const cacheElementsFromResponse = (htmlMarkup) => {
  if (!htmlMarkup) return;
  const products = Array.from(htmlMarkup.querySelectorAll('.product-grid-item'));
  const collections = htmlMarkup.querySelector('.collections');
  return {
    hasNextPage: collections?.dataset?.hasNextPage,
    count: products[products.length - 1]?.dataset?.index,
    elements: {
      sectionWrapper: htmlMarkup.getElementById(`section-wrapper-${window.Collection.section.id}`),
      collectionList: htmlMarkup.getElementById('collection-list'),
      filtersDrawer: htmlMarkup.querySelector('.filters__drawer'),
      filtersCount: htmlMarkup.querySelector('.filters__count'),
      collections,
      products,
    }
  }
}

const initLoadedProducts = (products, updateUI=false, position = 'beforeend') => {
  products.forEach((product) => {
    const isPromoBanner = product.classList.contains('collections__promoBanner');

    if (updateUI) {
      state.elements.collectionList.insertAdjacentElement(position, product);
    }

    if (isPromoBanner) {
      setPromoBannerMediaSrc(product);
    } else {
      new ProductCard(product);
    }
  });
};

const setPromoBannerMediaSrc = (banner) => {
  const video = banner.querySelector('video');
  const isMobile = window.matchMedia('(max-width: 750px)').matches;
  if (video && isMobile) {
    const mobileSrc = video.dataset.mobSrc;
    video.setAttribute('src', mobileSrc);
  }
}

const resetProducts = () => {
  state.elements.collectionList.innerHTML = '';
  window.products = {};
}

const addProductsToWindow = (htmlMarkup) => {
  const scriptTag = htmlMarkup.querySelector('script[data-products]');
  let initProducts = new Function(scriptTag?.textContent);
  initProducts();
};

export const loadNewProducts = async (filtersChanged = false) => {
  state.elements.loader.classList.add('active');
  hideLoadMoreBtn(true)
  if (filtersChanged) {
    state.elements.emptyCollectionContainer.classList.remove('active');
  }

  const targetUrl = getFetchUrl();
  const htmlMarkup = await fetchProducts(targetUrl);
  if (!htmlMarkup) return;
  const data = cacheElementsFromResponse(htmlMarkup)
  if (!data.elements.collections) {
    disconnectObserver();
    state.elements.sectionWrapper.innerHTML = data.elements.sectionWrapper.innerHTML;
  }
  addProductsToWindow(htmlMarkup);
  initLoadedProducts(data.elements.products, true);
  if (filtersChanged) {
    updateFilters(data.elements.filtersCount?.textContent, data.elements.filtersDrawer);
    updateSelectedFilters(htmlMarkup);
  }

  if (+data.elements.filtersCount.dataset.value < 1) {
    state.elements.emptyCollectionContainer.classList.add('active');
  }
  updateObserverAndBtnAfterLoad(data)
  state.elements.loader.classList.remove('active');
  hideLoadMoreBtn(!data.hasNextPage);
  initializeProductOption();
  return data.hasNextPage;
};

const updateObserverAndBtnAfterLoad = (data) => {
  if (data.hasNextPage) {
    handleObserver(data);
    handleLoadMoreButton(data);
  } else {
    disconnectObserver();
  }
};

const handleObserver = () => {
  if (!state.observing) {
    initObserver();
  }
}

const handleLoadMoreButton = (data) => {
  if (!state.isInfiniteScroll && data.count % state.productCountBeforeBtn === 0) {
    hideLoadMoreBtn(false)
  }
}


// OTHER HELPERS

const onScrollBottom = async (intersectionEntries) => {
  if (state.loading || (intersectionEntries && !intersectionEntries[0].isIntersecting)) return;
  state.loading = true;
  const hasNextPage = await loadNewProducts();
  if (hasNextPage) {
    state.currentPage++;
  }
  const newParams = new URLSearchParams(window.location.search);
  newParams.set('page', state.currentPage);
  updateUrl(newParams.toString());
  state.loading = false;
};


const updateUrl = (params) => {
  const currentUrl = new URL(window.location.href);
  const newUrl = `${currentUrl.origin}${currentUrl.pathname}?${params}`;
  window.history.replaceState(null, null, newUrl);
};


const onParamChange = debounce(async (e) => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
  
  state.currentPage = 0;
  state.loading = true;
  state.elements.collectionList.innerHTML = '';
  updateUrl(e.detail.params);

  const filtersChanged = e?.detail?.filtersChanged;
  if (filtersChanged) {
    state.elements.filtersCount.classList.add('loading');
  }

  resetProducts();
  await loadNewProducts(filtersChanged);
  state.currentPage++;
  state.loading = false;
  state.elements.filtersCount.classList.remove('loading');
}, 350);

const getFetchUrl = (page) => {
  const currentUrl = new URL(window.location.href);
  const searchParams = new URLSearchParams(currentUrl.search);
  searchParams.set('page', page || state.currentPage + 1);
  searchParams.set('section_id', window.Collection.section.id);

  return `${currentUrl.origin}${
    currentUrl.pathname
  }?${searchParams.toString()}`;
};

const scrollToLastProduct = () => {
  const savedProduct = sessionStorage.getItem('plpHistory:product');
  const savedCollection = sessionStorage.getItem('plpHistory:collection');
  if (!savedProduct || !savedCollection) return;
  if (savedCollection === window.Collection.handle) {
    const targetProduct = document.querySelector(`.product-grid-item[data-id="${savedProduct}"]`);
    if (targetProduct) {
      window.scrollTo({ top: targetProduct.offsetTop - 100, behavior: 'auto' });
    }
  }
};

document.addEventListener("DOMContentLoaded", scrollToLastProduct);
window.addEventListener('load', scrollToLastProduct);
window.addEventListener('pageshow', scrollToLastProduct);

export default init